import { Suspense, useEffect, useState, useSyncExternalStore } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

export type BrowserApplicationModule = Readonly<{ default: ComponentType }>;
export type BrowserFieldReadiness = (signal: AbortSignal) => Promise<void>;

export interface BrowserFieldBootstrapProps {
  Application: LazyExoticComponent<ComponentType>;
  establishOnlineReadiness: BrowserFieldReadiness;
}

let connectivityRevision = 0;

function subscribeConnectivity(onStoreChange: () => void): () => void {
  const changed = () => {
    connectivityRevision += 1;
    onStoreChange();
  };
  window.addEventListener('online', changed);
  window.addEventListener('offline', changed);
  return () => {
    window.removeEventListener('online', changed);
    window.removeEventListener('offline', changed);
  };
}

function connectivitySnapshot(): string {
  return `${connectivityRevision}:${navigator.onLine ? 'online' : 'offline'}`;
}

function serverConnectivitySnapshot(): string {
  return 'server:offline';
}

function OfflineUnavailable() {
  return (
    <main style={{ boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif', margin: '0 auto', maxWidth: 680, minHeight: '100vh', padding: 'clamp(2rem, 8vw, 6rem) 1.5rem' }}>
      <h1>Offline field access unavailable</h1>
      <p role="status" aria-live="polite">
        Reconnect before opening CHAMBER. This browser shell is installed, but this device
        does not yet have an approved offline grant.
      </p>
      <p>No participant, measurement, queue, or locally retained workflow data was opened. Bluetooth peer sharing is not available in this build.</p>
    </main>
  );
}

function OnlineUnavailable({ retry }: Readonly<{ retry: () => void }>) {
  return (
    <main style={{ boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif', margin: '0 auto', maxWidth: 680, minHeight: '100vh', padding: 'clamp(2rem, 8vw, 6rem) 1.5rem' }}>
      <h1>CHAMBER is unavailable</h1>
      <p role="alert">The authorized session and API could not be verified. No governed application module was opened.</p>
      <button type="button" onClick={retry}>Try again</button>
    </main>
  );
}

/**
 * Keeps the static shell separate from governed application modules. Browser
 * connectivity merely permits an existing session/API readiness check; only a
 * successful authoritative check permits the lazy application import.
 */
export function BrowserFieldBootstrap({ Application, establishOnlineReadiness }: BrowserFieldBootstrapProps) {
  const connection = useSyncExternalStore(subscribeConnectivity, connectivitySnapshot, serverConnectivitySnapshot);
  const online = connection.endsWith(':online');
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Readonly<{
    phase: 'checking' | 'ready' | 'unavailable';
    connection: string;
  }>>({ phase: 'checking', connection: '' });

  useEffect(() => {
    if (!online) return;
    const controller = new AbortController();
    let current = true;
    setState({ phase: 'checking', connection });
    void establishOnlineReadiness(controller.signal).then(
      () => {
        if (current && !controller.signal.aborted && navigator.onLine && connectivitySnapshot() === connection)
          setState({ phase: 'ready', connection });
      },
      () => {
        if (current && !controller.signal.aborted && navigator.onLine && connectivitySnapshot() === connection)
          setState({ phase: 'unavailable', connection });
      },
    );
    return () => {
      current = false;
      controller.abort();
    };
  }, [attempt, connection, establishOnlineReadiness, online]);

  if (!online) return <OfflineUnavailable />;
  if (state.connection !== connection || state.phase === 'checking')
    return <main role="status">Verifying the authorized CHAMBER service…</main>;
  if (state.phase === 'unavailable') return <OnlineUnavailable retry={() => setAttempt((value) => value + 1)} />;
  return (
    <Suspense fallback={<main role="status">Loading CHAMBER…</main>}>
      <Application />
    </Suspense>
  );
}
